import React, {
  useState, useEffect, useContext
} from 'react';
import { useStore, ExperienceContext } from '@thd-nucleus/experience-context';
import { bool, string } from 'prop-types';
import {
  alias,
  arrayOf,
  params,
  arrayOf as arrayOfType,
  number as numberType,
  shape,
  string as stringType,
  useDataModel,
  useLazyDataModel,
  extend
} from '@thd-nucleus/data-sources';
import { Button } from '@thd-olt-component-react/core-ui';
import { Drawer, DrawerItem, DrawerHeader } from '@thd-olt-component-react/drawer';
import { Loading } from './shared/Loading';
import { SuccessDrawer } from './drawers/SuccessDrawer';
import { ErrorDrawer } from './drawers/ErrorDrawer';
import { AddToQuoteDrawer } from './drawers/AddToQuoteDrawer';
import { CreateQuoteDrawer } from './drawers/CreateQuoteDrawer';
import { PriceChangeWarningDrawer } from './drawers/PriceChangeWarningDrawer';
import { SignInDrawer } from './drawers/SignInDrawer';

import {
  DRAWERS,
  GENERIC_ERROR,
  MOBILE_CHANNEL,
  DEFAULT_SELECTED_QUOTE,
  QUOTE_ACTIVE_STATUS,
  QUOTE_PENDING_STATUS,
  ADMIN_ROLE
} from './shared/Constants';

const customerInfoCache = {
  svocId: null,
  userId: null
};

const AddToQuote = ({
  disabled,
  itemId,
  quantity,
  fulfillmentLocation,
  fulfillmentMethod
}) => {
  const { channel } = useContext(ExperienceContext);
  const { storeId, storeZip: zipCode } = useStore();
  const [drawerShowing, setDrawerShowing] = useState(false);
  const [error, setError] = useState(null);
  const [quoteName, setQuoteName] = useState('');
  const [successful, setSuccess] = useState(false); // used because of refetch causing loading
  const [selectedQuote, setSelectedQuote] = useState(DEFAULT_SELECTED_QUOTE);
  const [quoteList, setQuoteList] = useState([]);
  const [quotesPageSize, setQuotesPageSize] = useState(20);
  const [showLoadMore, setShowLoadMore] = useState(false);

  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('add-to-quote.ready'); }, []);

  const { data } = useDataModel('clientOnlyProduct', {
    variables: {
      itemId,
      storeId,
      zipCode,
    },
    skip: !itemId,
  });

  const { identifiers, media, pricing } = data?.product || {};
  const { brandName, productLabel } = identifiers || {};
  const mediaImages = media?.images || [];
  const mainImage = mediaImages[0] || '';

  if (!customerInfoCache.svocID) {
    if (typeof window !== 'undefined') {
      const { svocID, userID, role } = window.THDCustomer?.default;
      customerInfoCache.svocId = svocID;
      customerInfoCache.userId = userID;
      customerInfoCache.role = role;
    }
  }

  const variables = {
    svocId: customerInfoCache.svocId,
    pageSize: quotesPageSize,
    userId: customerInfoCache.userId
  };

  const [
    getAllQuotes,
    { data: quoteData, loading: quoteLoading, error: quoteError }
  ] = useLazyDataModel('getQuoteList', {
    variables,
  });

  const toggleDrawer = () => {
    if (!quoteData && !quoteError) {
      getAllQuotes();
    }
    setDrawerShowing(!drawerShowing);
  };

  const { totalNbrOfQuotes } = quoteData?.getQuoteList || {};

  useEffect(() => {
    if (drawerShowing && quoteData && (!successful && !quoteError)) {
      if (totalNbrOfQuotes > 0) {
        window.LIFE_CYCLE_EVENT_BUS.trigger('add-to-quote.addToQuoteOverlay');
      } else if (totalNbrOfQuotes === 0) {
        window.LIFE_CYCLE_EVENT_BUS.trigger('add-to-quote.createAQuoteOverlay');
      }
    }
  }, [quoteData, drawerShowing]);

  const getInitialItem = () => {
    if (quoteError && /AUTH/.test(quoteError.message)) return DRAWERS.SIGN_IN_WARNING;
    if (!quoteLoading && quoteError) return DRAWERS.ERROR;
    if (successful) return DRAWERS.SUCCESS;
    return !quoteLoading && quoteList?.length > 0 ? DRAWERS.ADD_TO_QUOTE : DRAWERS.CREATE_QUOTE;
  };

  const getErrorMessage = () => {
    if (!error) return '';
    const { message, graphQLErrors } = error;

    // check is API error message
    if (!message || message === 'null') {
      if (graphQLErrors.length) {
        const { extensions } = graphQLErrors[0];
        const { errorData } = extensions;
        if (errorData.length) {
          const { errors } = errorData[0];
          if (errors.length) return errors[0].description;
        }
      }
    }

    // Check if generic from server without helpful info
    if (/(50|51|40|41)/.test(message)) return GENERIC_ERROR;

    // Otherwise return the message
    return error.message;
  };

  const isAccessTypeValid = (accessType) => {
    // hide if accessType on 1 or 2, show if null or not 1 or 2
    return !accessType || (accessType && (accessType !== 1 && accessType !== 2));
  };

  const statusIsValid = (status) => {
    const upperCased = status.toUpperCase();
    return upperCased === QUOTE_ACTIVE_STATUS || upperCased === QUOTE_PENDING_STATUS;
  };

  const shouldShowItem = ({ status, accessType }) => {
    return statusIsValid(status) && isAccessTypeValid(accessType);
  };

  useEffect(() => {
    if (quotesPageSize > 20) {
      getAllQuotes();
    }
    const quotes = quoteData?.getQuoteList?.quotes || [];
    const filteredQuotes = quotes.filter(({ status, accessType }) => {
      return shouldShowItem({ status, accessType });
    }) || [];
    setQuoteList(filteredQuotes);
    const index = quotes.length;
    if (quotes[index - 1]?.status === 'Expired' || totalNbrOfQuotes === quoteList?.length) {
      setShowLoadMore(false);
    } else {
      setShowLoadMore(true);
    }
  }, [quotesPageSize]);

  useEffect(() => {
    const quotes = quoteData?.getQuoteList?.quotes || [];
    const filteredQuotes = quotes.filter(({ status, accessType }) => {
      return shouldShowItem({ status, accessType });
    }) || [];
    if (drawerShowing && !quoteList.length && filteredQuotes.length) {
      setQuoteList(filteredQuotes);
    }
    const index = quotes.length - 1;
    if (quotes[index]?.status === 'Expired' || totalNbrOfQuotes === quoteList?.length) {
      setShowLoadMore(false);
    } else {
      setShowLoadMore(true);
    }
  }, [drawerShowing, quoteList, quoteData]);

  return (
    <>
      <Button onClick={toggleDrawer} outline disabled={disabled}>Add to Quote</Button>
      {data?.product && (
        <Drawer
          open={drawerShowing}
          onClose={() => {
            setDrawerShowing(false);
            setSelectedQuote(DEFAULT_SELECTED_QUOTE);
            setQuoteList([]);
            setQuotesPageSize(20);
          }}
          initialItem={quoteLoading ? DRAWERS.LOADING : getInitialItem()}
          isMobile={channel === MOBILE_CHANNEL}
          position="right"
          hideMask={channel === MOBILE_CHANNEL}
          maxSize={channel === MOBILE_CHANNEL ? 100 : 400}
        >
          <DrawerHeader title="Add to Quote" />
          <DrawerItem name={DRAWERS.LOADING}>
            <Loading active />
          </DrawerItem>
          <DrawerItem name={DRAWERS.ADD_TO_QUOTE}>
            <AddToQuoteDrawer
              productLabel={productLabel}
              brandName={brandName}
              mainImage={mainImage}
              quoteList={quoteList}
              setSelectedQuote={setSelectedQuote}
              selectedQuote={selectedQuote}
              showLoadMore={showLoadMore}
              quotesPageSize={quotesPageSize}
              setQuotesPageSize={setQuotesPageSize}
            />
          </DrawerItem>
          <DrawerItem name={DRAWERS.CREATE_QUOTE}>
            <CreateQuoteDrawer
              itemId={itemId}
              customerInfo={customerInfoCache}
              productLabel={productLabel}
              price={pricing?.value}
              brandName={brandName}
              mainImage={mainImage}
              quantity={quantity}
              fulfillmentMethod={fulfillmentMethod}
              fulfillmentLocation={fulfillmentLocation}
              localStoreId={storeId}
              setError={setError}
              setQuoteName={setQuoteName}
              setSuccess={setSuccess}
            />
          </DrawerItem>
          <DrawerItem name={DRAWERS.PRICE_CHANGE_WARNING}>
            <PriceChangeWarningDrawer
              itemId={itemId}
              quantity={quantity}
              price={pricing?.value}
              fulfillmentLocation={fulfillmentLocation}
              fulfillmentMethod={fulfillmentMethod}
              localStoreId={storeId}
              customerInfo={customerInfoCache}
              setQuoteName={setQuoteName}
              setError={setError}
              selectedQuote={selectedQuote}
            />
          </DrawerItem>
          <DrawerItem name={DRAWERS.SIGN_IN_WARNING}>
            <SignInDrawer />
          </DrawerItem>
          <DrawerItem name={DRAWERS.SUCCESS}>
            <SuccessDrawer
              quoteName={quoteName}
              setQuoteName={setQuoteName}
              setSelectedQuote={setSelectedQuote}
              setSuccess={setSuccess}
            />
          </DrawerItem>
          <DrawerItem name={DRAWERS.ERROR}>
            <ErrorDrawer message={getErrorMessage()} />
          </DrawerItem>
        </Drawer>
      )}
    </>
  );
};

AddToQuote.propTypes = {
  disabled: bool,
  itemId: string.isRequired,
  fulfillmentLocation: string.isRequired,
  fulfillmentMethod: string.isRequired,
  quantity: string.isRequired
};

AddToQuote.defaultProps = {
  disabled: false,
};

AddToQuote.dataModel = extend(
  {
    clientOnlyProduct: alias('product').params({
      itemId: stringType().isRequired()
    }).shape({
      itemId: stringType(),
      dataSources: stringType(),
      identifiers: shape({
        productLabel: stringType(),
        parentId: stringType(),
        brandName: stringType()
      }),
      media: shape({
        images: shape({
          url: stringType(),
          sizes: arrayOfType(stringType())
        })
      }),
      pricing: shape({
        value: numberType({ float: true })
      })
    })
  },
  {
    getQuoteList: params({
      svocId: stringType().isRequired(),
      userId: stringType(),
      pageNo: stringType(),
      pageSize: stringType(),
    }).shape({
      userId: stringType(),
      customerAccountId: stringType(),
      totalNbrOfQuotes: numberType({ int: true }),
      totalNbrOfPages: numberType({ int: true }),
      quotes: arrayOf(
        shape({
          name: stringType(),
          status: stringType(),
          quoteId: stringType(),
          userId: stringType(),
          accessType: numberType()
        })
      ),
      errorData: arrayOf(
        shape({
          errors: arrayOf(
            shape({
              id: stringType(),
              description: stringType(),
              submessage: stringType(),
              errorCode: stringType(),
              errorProperties: shape({
                errorProperty: shape({
                  name: stringType(),
                  value: stringType()
                })
              })
            })
          ),
          httpStatus: stringType(),
          userId: stringType(),
          customerAccountId: stringType(),
          correlationId: stringType(),
          correlationType: stringType()
        })
      )
    })
  },
  CreateQuoteDrawer,
  PriceChangeWarningDrawer
);

export { AddToQuote };
